import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { determineLocale, getTranslatedTextByLocale } from "../../contexts/LocaleContext";
import { EN_US, formatTimeAsString, HOUR12 } from "../../utils/timeUtil";


type OpeningHours = {
  openfrom: string; // "HH:mm"
  closeat?: string; // "HH:mm" | undefined
};

type PartnerDestinationDetails = {
  opening_hours?: OpeningHours;
};

type StatusResult = {
  status: 'Open' | 'Closed' | 'Closing Soon' | 'Open 24 Hours';
  text: string;
};

type ParsedTime = {
  hours: number;
  minutes: number;
  raw: string;
};

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

function parseTime(time: string): ParsedTime {
  const [hours, minutes] = time.split(':').map(Number);
  return {
    hours,
    minutes,
    raw: time.replace(':', ''),
  };
}

function buildDateFromTime(base: Date, time: ParsedTime): Date {
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate(),
    time.hours,
    time.minutes
  );
}


export const adjustTimeForTimeZones = (
  now: Date,
  targetUtcOffsetMinutes: number
): void => {
  const userUtcOffset = now.getTimezoneOffset();
  const offsetDifference = targetUtcOffsetMinutes - userUtcOffset;
  now.setMinutes(now.getMinutes() - offsetDifference);
};

export const setOperatingHoursFromPartnerDetails = (
  partnerDestinationDetails: PartnerDestinationDetails
): StatusResult => {
  const locale = determineLocale();

  const operatingHours = partnerDestinationDetails.opening_hours;

  if (!operatingHours?.openfrom) {
    return {
      status: 'Closed',
      text: getTranslatedTextByLocale(
        locale,
        TRANSLATION_CONSTANTS.STATUS_CLOSED
      ),
    };
  }

  const openTime = parseTime(operatingHours.openfrom);
  const closeTime = operatingHours.closeat
    ? parseTime(operatingHours.closeat)
    : null;

  // Open 24 hours case
  const is24Hours =
    openTime.raw === '0000' && !operatingHours.closeat;

  if (is24Hours) {
    return {
      status: 'Open 24 Hours',
      text: getTranslatedTextByLocale(
        locale,
        TRANSLATION_CONSTANTS.STATUS_OPEN
      ),
    };
  }

  const now = new Date();
  const utcOffsetMinutes = now.getTimezoneOffset();
  adjustTimeForTimeZones(now, utcOffsetMinutes);

  if (!closeTime) {
    return {
      status: 'Closed',
      text: getTranslatedTextByLocale(
        locale,
        TRANSLATION_CONSTANTS.STATUS_CLOSED
      ),
    };
  }

  const openingDate = buildDateFromTime(now, openTime);
  const closingDate = buildDateFromTime(now, closeTime);

  let status: StatusResult['status'];
  let text: string;

  if (now < openingDate) {
    status = 'Closed';
    text = getTranslatedTextByLocale(
      locale,
      TRANSLATION_CONSTANTS.STATUS_CLOSED_OPENSAT
    ).replace(
      '{{time}}',
      formatTimeAsString(openingDate, EN_US, HOUR12)
    );
  } else if (
    closingDate.getTime() - now.getTime() <= THIRTY_MINUTES_MS &&
    closingDate > now
  ) {
    status = 'Closing Soon';
    text = getTranslatedTextByLocale(
      locale,
      TRANSLATION_CONSTANTS.STATUS_CLOSING_SOON
    ).replace(
      '{{time}}',
      formatTimeAsString(closingDate, EN_US, HOUR12)
    );
  } else if (closingDate > now) {
    status = 'Open';
    text = getTranslatedTextByLocale(
      locale,
      TRANSLATION_CONSTANTS.STATUS_OPEN
    ).replace(
      '{{time}}',
      formatTimeAsString(closingDate, EN_US, HOUR12)
    );
  } else {
    status = 'Closed';
    text = getTranslatedTextByLocale(
      locale,
      TRANSLATION_CONSTANTS.STATUS_CLOSED
    );
  }

  return { status, text };
};